import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@components/common/Button';
import { ExpressionNumberInput } from '@components/common/ExpressionNumberInput';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { DocumentFieldsResponse, ExtractionStatusResponse } from '@schemas/api';

type TaxItem = NonNullable<DocumentFieldsResponse['tax_items']>[number];

interface TaxItemsEditorProps {
  items: TaxItem[];
  onChange: (items: TaxItem[]) => void;
  isLoading?: boolean;
  className?: string;
  taxIdTypes?: NonNullable<ExtractionStatusResponse['tax_id_types']>;
}

const DEFAULT_TAX_ITEM: TaxItem = {
  label: '',
  rate: null,
  base_amount: null,
  amount: null,
  scope: '',
  tax_registration_number: '',
  tax_registration_number_source: '',
  raw_text: '',
  tax_id_key: '',
  tax_id_label: '',
  tax_percent: null,
};

const roundToDisplayPrecision = (value: number): number =>
  Number(value.toFixed(4));

const formatTaxPercent = (taxPercent?: number | null): string => {
  if (taxPercent === null || taxPercent === undefined) {
    return 'varies';
  }
  const rounded = Number(taxPercent.toFixed(3));
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}%`;
};

const toDisplayedRate = (rate: number | null | undefined): number | '' => {
  if (rate === null || rate === undefined) {
    return '';
  }

  return roundToDisplayPrecision(Math.abs(rate) <= 1 ? rate * 100 : rate);
};

const toStoredRate = (
  displayedRate: number | string | null,
  previousRate: number | null | undefined
): number | null => {
  if (displayedRate === '' || displayedRate === null) {
    return null;
  }

  const parsedRate = typeof displayedRate === 'number' ? displayedRate : parseFloat(displayedRate);
  if (Number.isNaN(parsedRate)) {
    return null;
  }

  if (previousRate !== null && previousRate !== undefined && Math.abs(previousRate) <= 1) {
    return parsedRate / 100;
  }

  return parsedRate;
};

const normalizeLookupText = (value: string): string =>
  value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const findTaxTypeByLabel = (
  taxIdTypes: NonNullable<ExtractionStatusResponse['tax_id_types']>,
  label: string,
  rate?: number | null
) => {
  const normalizedLabel = normalizeLookupText(label);
  if (!normalizedLabel) {
    return null;
  }

  const candidates = taxIdTypes.filter((entry) => {
    const tokens = [entry.label, ...(entry.aliases || [])];
    return tokens.some((token) => normalizedLabel.includes(normalizeLookupText(token)));
  });

  if (candidates.length === 0) {
    return null;
  }

  const exactRateMatches = rate === null || rate === undefined
    ? []
    : candidates.filter((entry) => entry.tax_percent !== null && entry.tax_percent !== undefined && Math.abs(Number(entry.tax_percent) - Number(rate)) < 0.001);

  const ranked = (exactRateMatches.length > 0 ? exactRateMatches : candidates).slice().sort((left, right) => {
    const leftIsGeneric = left.tax_percent === null || left.tax_percent === undefined;
    const rightIsGeneric = right.tax_percent === null || right.tax_percent === undefined;
    if (leftIsGeneric !== rightIsGeneric) {
      return Number(leftIsGeneric) - Number(rightIsGeneric);
    }
    return left.label.length - right.label.length;
  });

  return ranked[0] || null;
};

export const TaxItemsEditor: React.FC<TaxItemsEditorProps> = ({
  items,
  onChange,
  isLoading = false,
  className = '',
  taxIdTypes = [],
}) => {
  const [draftItems, setDraftItems] = useState<TaxItem[]>(() => items);
  const serializedItems = useMemo(() => JSON.stringify(items), [items]);
  const lastPropSnapshotRef = useRef(serializedItems);
  const taxLineTaxIdTypes = useMemo(
    () => taxIdTypes.filter((entry) => entry.category !== 'taxpayer_identity'),
    [taxIdTypes]
  );

  useEffect(() => {
    if (serializedItems !== lastPropSnapshotRef.current) {
      lastPropSnapshotRef.current = serializedItems;
      setDraftItems(items);
    }
  }, [items, serializedItems]);

  const emitChange = useCallback((nextItems: TaxItem[]) => {
    setDraftItems(nextItems);
    onChange(nextItems);
  }, [onChange]);

  const handleAddTaxItem = useCallback(() => {
    emitChange([...draftItems, { ...DEFAULT_TAX_ITEM }]);
  }, [draftItems, emitChange]);

  const handleDeleteTaxItem = useCallback((index: number) => {
    emitChange(draftItems.filter((_, currentIndex) => currentIndex !== index));
  }, [draftItems, emitChange]);

  const handleCellChange = useCallback((index: number, field: keyof TaxItem, value: string | number | null) => {
    const nextItems = [...draftItems];
    nextItems[index] = {
      ...nextItems[index],
      [field]: value,
    };
    emitChange(nextItems);
  }, [draftItems, emitChange]);

  const handleNumericChange = useCallback((index: number, field: keyof TaxItem, value: number | string | null) => {
    handleCellChange(index, field, value === '' ? null : value);
  }, [handleCellChange]);

  const handleRateChange = useCallback((index: number, value: number | string | null) => {
    const previousRate = draftItems[index]?.rate;
    handleCellChange(index, 'rate', toStoredRate(value, previousRate));
  }, [draftItems, handleCellChange]);

  const taxTypeByKey = useMemo(
    () => new Map(taxLineTaxIdTypes.map((entry) => [entry.key, entry])),
    [taxLineTaxIdTypes]
  );

  const getSelectedTaxType = useCallback((item: TaxItem) => {
    const key = String(item.tax_id_key ?? '').trim();
    if (key && taxTypeByKey.has(key)) {
      return taxTypeByKey.get(key) || null;
    }
    const labelMatched = findTaxTypeByLabel(taxLineTaxIdTypes, String(item.label ?? ''), item.rate ?? null);
    if (labelMatched) {
      return labelMatched;
    }
    return null;
  }, [taxLineTaxIdTypes, taxTypeByKey]);

  const getSelectedTaxTypeKey = useCallback((item: TaxItem): string => {
    return getSelectedTaxType(item)?.key ?? '__custom__';
  }, [getSelectedTaxType]);

  const handleTaxTypeChange = useCallback((index: number, nextKey: string) => {
    const currentItem = draftItems[index];
    if (!currentItem) {
      return;
    }

    if (nextKey === '__custom__') {
      emitChange(
        draftItems.map((item, currentIndex) =>
          currentIndex === index
            ? {
              ...item,
              tax_id_key: null,
              tax_id_label: null,
              tax_percent: null,
            }
            : item
        )
      );
      return;
    }

    const selected = taxTypeByKey.get(nextKey);
    if (!selected) {
      return;
    }

    emitChange(
      draftItems.map((item, currentIndex) => {
        if (currentIndex !== index) {
          return item;
        }
        const nextItem: TaxItem = {
          ...item,
          label: selected.label,
          tax_id_key: selected.key,
          tax_id_label: selected.label,
          tax_percent: selected.tax_percent ?? null,
        };
        if (selected.tax_percent !== null && selected.tax_percent !== undefined) {
          nextItem.rate = selected.tax_percent;
        }
        return nextItem;
      })
    );
  }, [draftItems, emitChange, taxTypeByKey]);

  const handleLabelChange = useCallback((index: number, nextLabel: string) => {
    const currentRate = draftItems[index]?.rate ?? null;
    const matched = findTaxTypeByLabel(taxLineTaxIdTypes, nextLabel, currentRate);

    if (!matched) {
      handleCellChange(index, 'label', nextLabel);
      return;
    }

    const nextItems = [...draftItems];
    const existing = nextItems[index];
    if (!existing) {
      return;
    }

    nextItems[index] = {
      ...existing,
      label: matched.label,
      tax_id_key: matched.key,
      tax_id_label: matched.label,
      tax_percent: matched.tax_percent ?? null,
      rate: matched.tax_percent ?? existing.rate ?? null,
    };
    emitChange(nextItems);
  }, [draftItems, emitChange, handleCellChange, taxLineTaxIdTypes]);

  return (
    <div className={`space-y-3 ${className}`}>
      {draftItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400">
          No tax lines
        </div>
      ) : (
        draftItems.map((item, index) => (
          <div
            key={`${item.label || 'tax-item'}-${index}`}
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Tax Line {index + 1}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleDeleteTaxItem(index)}
                disabled={isLoading}
                className="justify-center"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Label
                </label>
                {taxLineTaxIdTypes.length > 0 ? (
                  <div className="space-y-2">
                    <SearchableSelect
                      value={getSelectedTaxTypeKey(item)}
                      onChange={(selectedValue) => handleTaxTypeChange(index, selectedValue)}
                      options={[
                        ...taxLineTaxIdTypes.map((taxType) => ({
                          id: taxType.key,
                          label: `${taxType.label} (${formatTaxPercent(taxType.tax_percent)})`,
                        })),
                        { id: '__custom__', label: 'Custom' },
                      ]}
                      placeholder="Tax label"
                      clearable={false}
                    />
                    {getSelectedTaxTypeKey(item) === '__custom__' ? (
                      <input
                        type="text"
                        value={item.label ?? ''}
                        onChange={(e) => handleLabelChange(index, e.target.value)}
                        placeholder="Custom tax label"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                    ) : null}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={item.label ?? ''}
                    onChange={(e) => handleLabelChange(index, e.target.value)}
                    placeholder="GST"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Scope
                </label>
                <input
                  type="text"
                  value={item.scope ?? ''}
                  onChange={(e) => handleCellChange(index, 'scope', e.target.value)}
                  placeholder="document_total"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Rate (%)
                </label>
                <ExpressionNumberInput
                  step="0.0001"
                  value={toDisplayedRate(item.rate)}
                  onValueChange={(value) => handleRateChange(index, value)}
                  emptyValue={null}
                  placeholder="9.975"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Base Amount
                </label>
                <ExpressionNumberInput
                  step="0.01"
                  value={item.base_amount ?? ''}
                  onValueChange={(value) => handleNumericChange(index, 'base_amount', value)}
                  emptyValue={null}
                  placeholder="125.00"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Tax Amount
                </label>
                <ExpressionNumberInput
                  step="0.01"
                  value={item.amount ?? ''}
                  onValueChange={(value) => handleNumericChange(index, 'amount', value)}
                  emptyValue={null}
                  placeholder="12.47"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Tax Registration Number
                </label>
                <input
                  type="text"
                  value={item.tax_registration_number ?? ''}
                  onChange={(e) => handleCellChange(index, 'tax_registration_number', e.target.value)}
                  placeholder="1234567890TQ0001"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Tax Registration Source
                </label>
                <input
                  type="text"
                  value={item.tax_registration_number_source ?? ''}
                  onChange={(e) => handleCellChange(index, 'tax_registration_number_source', e.target.value)}
                  placeholder="tax_line"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                  Raw Text
                </label>
                <textarea
                  value={item.raw_text ?? ''}
                  onChange={(e) => handleCellChange(index, 'raw_text', e.target.value)}
                  placeholder="QST 9.975% 12.47"
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>
          </div>
        ))
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={handleAddTaxItem}
          disabled={isLoading}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Tax Line
        </Button>
      </div>
    </div>
  );
};

export default TaxItemsEditor;
